import type {SerializedError} from '@reduxjs/toolkit';
import type {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {Actions, ActionTypes} from '~/ViewInvitation/components/Guestlist/actions/actions';
import {filterBy, queryBy, sortBy} from '~/ViewInvitation/components/Guestlist/helpers/utils';
import {type GuestSummary, LockedStatus} from '~/ViewInvitation/types/api';
import type {Guest, RsvpResponse} from '~/ViewInvitation/types/guests';
import {SortBy, type SortDirection} from '~/ViewInvitation/types/reducer';

export interface State {
  guestlist: Guest[];
  all: Guest[];
  sortDirection: SortDirection;
  sortType: SortBy;
  query: string | undefined;
  rsvpResponse: RsvpResponse | undefined;
  summary: GuestSummary;
  isLoading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
}

export const defaultSummaryValue = {
  no: 0,
  noReply: 0,
  adultsYes: 0,
  kidsYes: 0,
  adultsMaybe: 0,
  kidsMaybe: 0,
  inviteesYes: 0,
  inviteesMaybe: 0,
  yes: 0,
  maybe: 0,
  lockedStatus: 'unlocked' as LockedStatus,
};

const defaultValues = {
  sortDirection: 'asc' as SortDirection,
  sortType: SortBy.ALPHA,
  query: undefined,
  rsvpResponse: undefined,
  isLoading: false,
  error: undefined,
};

export const initialState: State = {
  guestlist: [],
  all: [],
  summary: defaultSummaryValue,
  ...defaultValues,
  isLoading: true,
};

export const reducer = (state: State, action: Actions): State => {
  switch (action.type) {
    case ActionTypes.SET_GUESTS: {
      let guestlist = filterBy(action.guests, state.rsvpResponse);
      guestlist = queryBy(guestlist, state.query);
      return {
        ...state,
        ...defaultValues,
        all: action.guests,
        guestlist: sortBy(guestlist, state.sortType, state.sortDirection),
        summary: action.summary,
        isLoading: false,
      };
    }
    case ActionTypes.SORT_GUESTS: {
      const sortDirection = action.sortDirection || state.sortDirection;
      let guestlist = filterBy(state.all, state.rsvpResponse);
      guestlist = queryBy(guestlist, state.query);
      return {
        ...state,
        sortType: action.sortType,
        sortDirection,
        guestlist: sortBy(guestlist, action.sortType, sortDirection),
      };
    }
    case ActionTypes.SORT_DIRECTION: {
      let guestlist = filterBy(state.all, state.rsvpResponse);
      guestlist = queryBy(guestlist, state.query);
      return {
        ...state,
        sortDirection: action.sortDirection,
        guestlist: sortBy(guestlist, state.sortType, action.sortDirection),
      };
    }
    case ActionTypes.FILTER_GUESTS: {
      let guestlist = filterBy(state.all, action.rsvpResponse);
      guestlist = queryBy(guestlist, state.query);
      return {
        ...state,
        rsvpResponse: action.rsvpResponse,
        guestlist: sortBy(guestlist, state.sortType, state.sortDirection),
      };
    }
    case ActionTypes.SEARCH_GUESTS: {
      let guestlist = filterBy(state.all, state.rsvpResponse);
      guestlist = queryBy(guestlist, action.query);
      return {
        ...state,
        query: action.query,
        guestlist: sortBy(guestlist, state.sortType, state.sortDirection),
      };
    }
    case ActionTypes.RESET_GUESTS: {
      return {
        ...state,
        ...defaultValues,
        guestlist: state.all,
        isLoading: false,
      };
    }
    case ActionTypes.SET_ERROR: {
      return {
        ...state,
        error: action.error,
        isLoading: false,
      };
    }
    case ActionTypes.CLEAR_ERROR: {
      return {
        ...state,
        error: undefined,
      };
    }
    default:
      return state;
  }
};
